import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Stack,
  Typography,
  Button,
  TextField,
  Card,
  TableRow,
  TableBody,
  TableContainer,
  Table,
  TableHead,
  TableCell,
  Pagination,
  Tabs,
  Tab,
} from '@mui/material';
import api from '../../api';
import { toast } from 'react-toastify';
import CircularProgressWithLabel from '../../utils/loader';

const INITIAL_PAGE_INFO = {
  page: 1,
  total_pages: 0,
  total: 0,
  current_page: 1,
};

const INITIAL_TODAY_QUERY = {
  consignmentNo: '',
  from: '',
  to: '',
};

const INITIAL_ALL_QUERY = {
  consignmentNo: '',
  from: '',
  to: '',
};

const formatDateTime = (value) => {
  if (!value) return 'NA';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'NA';
  return date.toLocaleString();
};

const normalizeListResponse = (data) => {
  const list =
    data?.deliveries ||
    data?.outForDeliveries ||
    data?.out_for_deliveries ||
    data?.todayOutForDeliveries ||
    data?.allOutForDeliveries ||
    data?.data ||
    data?.items ||
    [];

  const pageInfo = data?.pageInfo || INITIAL_PAGE_INFO;
  return {
    list: Array.isArray(list) ? list : [],
    pageInfo: { ...INITIAL_PAGE_INFO, ...pageInfo },
  };
};

const fetchOutForDelivery = async (page, query, isTodayTab) => {
  const endpoint = isTodayTab
    ? '/api/v1/deliveries/today_out_for_delivery'
    : '/api/v1/deliveries/all_out_for_delivery';

  const response = await api.post(endpoint, { query, page });
  if (response?.data?.success) {
    return normalizeListResponse(response.data);
  }
  throw new Error(response?.data?.message || 'Unable to load out-for-delivery packages.');
};

export default function OutOfDeliveryPackages() {
  const [activeTab, setActiveTab] = useState(0);

  const [todayList, setTodayList] = useState([]);
  const [todayLoading, setTodayLoading] = useState(false);
  const [todayPageInfo, setTodayPageInfo] = useState(INITIAL_PAGE_INFO);
  const [todayQuery, setTodayQuery] = useState(INITIAL_TODAY_QUERY);

  const [allList, setAllList] = useState([]);
  const [allLoading, setAllLoading] = useState(false);
  const [allPageInfo, setAllPageInfo] = useState(INITIAL_PAGE_INFO);
  const [allQuery, setAllQuery] = useState(INITIAL_ALL_QUERY);

  const isTodayTab = activeTab === 0;
  const loading = isTodayTab ? todayLoading : allLoading;
  const rows = isTodayTab ? todayList : allList;
  const pageInfo = isTodayTab ? todayPageInfo : allPageInfo;
  const selectedQuery = useMemo(
    () => (isTodayTab ? todayQuery : allQuery),
    [isTodayTab, todayQuery, allQuery]
  );

  const loadData = async (tab, pageToLoad, queryToUse) => {
    const todayTab = tab === 0;
    try {
      if (todayTab) setTodayLoading(true);
      else setAllLoading(true);

      const { list, pageInfo: nextPageInfo } = await fetchOutForDelivery(pageToLoad, queryToUse, todayTab);
      if (todayTab) {
        setTodayList(list);
        setTodayPageInfo(nextPageInfo);
      } else {
        setAllList(list);
        setAllPageInfo(nextPageInfo);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Unable to load out-for-delivery packages.');
      if (todayTab) setTodayList([]);
      else setAllList([]);
    } finally {
      if (todayTab) setTodayLoading(false);
      else setAllLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 0) {
      loadData(0, todayPageInfo.current_page || 1, todayQuery);
      return;
    }
    loadData(1, allPageInfo.current_page || 1, allQuery);
  }, [activeTab]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleQueryChange = (event) => {
    const { name, value } = event.target;
    if (isTodayTab) {
      setTodayQuery((prev) => ({ ...prev, [name]: value }));
      return;
    }
    setAllQuery((prev) => ({ ...prev, [name]: value }));
  };

  const onClickSearch = () => {
    if (isTodayTab) {
      setTodayPageInfo((prev) => ({ ...prev, page: 1, current_page: 1 }));
      loadData(0, 1, todayQuery);
      return;
    }
    setAllPageInfo((prev) => ({ ...prev, page: 1, current_page: 1 }));
    loadData(1, 1, allQuery);
  };

  const onClickReset = () => {
    if (isTodayTab) {
      setTodayQuery(INITIAL_TODAY_QUERY);
      setTodayPageInfo(INITIAL_PAGE_INFO);
      loadData(0, 1, INITIAL_TODAY_QUERY);
      return;
    }
    setAllQuery(INITIAL_ALL_QUERY);
    setAllPageInfo(INITIAL_PAGE_INFO);
    loadData(1, 1, INITIAL_ALL_QUERY);
  };

  const onPageChange = (event, newPage) => {
    if (isTodayTab) {
      setTodayPageInfo((prev) => ({ ...prev, page: newPage, current_page: newPage }));
      loadData(0, newPage, todayQuery);
      return;
    }
    setAllPageInfo((prev) => ({ ...prev, page: newPage, current_page: newPage }));
    loadData(1, newPage, allQuery);
  };

  return (
    <Container maxWidth={false}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h4" gutterBottom>
          Out Of Delivery Packages
        </Typography>
      </Stack>

      <Card sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Today Out Of Delivery" />
          <Tab label="All Out Of Deliveries" />
        </Tabs>
      </Card>

      <Card sx={{ mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ p: 2 }}>
          <TextField
            label="Consignment Number"
            name="consignmentNo"
            size="small"
            fullWidth
            value={selectedQuery.consignmentNo}
            onChange={handleQueryChange}
          />
          <TextField
            label="From"
            name="from"
            type="date"
            size="small"
            fullWidth
            value={selectedQuery.from}
            onChange={handleQueryChange}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="To"
            name="to"
            type="date"
            size="small"
            fullWidth
            value={selectedQuery.to}
            onChange={handleQueryChange}
            InputLabelProps={{ shrink: true }}
          />
          <Button variant="contained" onClick={onClickSearch}>
            Search
          </Button>
          <Button variant="outlined" onClick={onClickReset}>
            Reset
          </Button>
        </Stack>
      </Card>

      <Card>
        {loading ? (
          <CircularProgressWithLabel />
        ) : (
          <>
            <TableContainer>
              <Table sx={{ minWidth: 900 }} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="center">SNo</TableCell>
                    <TableCell align="center">Consignment Number</TableCell>
                    <TableCell align="center">Consignee Address</TableCell>
                    <TableCell align="center">Consignee Contact</TableCell>
                    <TableCell align="center">Sender Name</TableCell>
                    <TableCell align="center">Sub Packages</TableCell>
                    <TableCell align="center">To Pay</TableCell>
                    <TableCell align="center">Total Amount</TableCell>
                    <TableCell align="center">Out For Delivery On</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length > 0 &&
                    rows.map((row, index) => (
                      <TableRow hover key={row?.id || `${row?.consignmentNo}-${index}`}>
                        <TableCell align="center">
                          {(parseInt(pageInfo.current_page || 1, 10) - 1) * 10 + index + 1}
                        </TableCell>
                        <TableCell align="center">
                          <b>{row?.consignmentNo || 'NA'}</b>
                        </TableCell>
                        <TableCell align="center">{row?.consigneeAddress || 'NA'}</TableCell>
                        <TableCell align="center">{row?.consigneeContact || row?.consigneeContactNo || 'NA'}</TableCell>
                        <TableCell align="center">{row?.senderName || 'NA'}</TableCell>
                        <TableCell align="center">
                          {Number(row?.subPackagesCount || 0) > 0
                            ? row.subPackages
                                ?.map((pkg) => pkg?.label)
                                .filter(Boolean)
                                .join(', ') || row?.subPackagesCount
                            : 0}
                        </TableCell>
                        <TableCell align="center">{row?.toPay ? 'Yes' : 'No'}</TableCell>
                        <TableCell align="center">{row?.toPay ? row?.totalAmount || '0' : '-'}</TableCell>
                        <TableCell align="center">
                          {formatDateTime(row?.outForDeliveryOn || row?.createdAt || row?.updatedAt)}
                        </TableCell>
                      </TableRow>
                    ))}
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ height: 200 }}>
                        <Typography variant="h6" color="text.secondary">
                          No out-of-delivery packages found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {rows.length > 0 && (
              <Stack alignItems="center" py={2}>
                <Pagination
                  color="primary"
                  onChange={onPageChange}
                  page={pageInfo.current_page || 1}
                  count={pageInfo.total_pages || 1}
                />
              </Stack>
            )}
          </>
        )}
      </Card>
    </Container>
  );
}
